import { Graphics, Sprite, Texture } from 'pixi.js';
import type { CircadianAttack, EclipseEffect } from '../../data/full/circadien';
import { rayWay, skyOf, starCount } from '../../sim/full/attacks/circadian';
import { ECLIPSE, eclipseRadius, eclipseRayWays } from '../../sim/full/skills/eclipse';
import type { PlayerId, PropState, SimEvent, SimState } from '../../sim/state';
import { statValue } from '../../sim/stats';
import type { Family, RenderContext } from '../context';
import type { Frame } from '../frame';
import { clamp01, lerp, smoothstep } from '../../shared/scalar';
import type { Vec2 } from '../../shared/vec';
import {
  type Ctx,
  type Piece,
  type Sheet,
  WHITE,
  circle,
  paintSheet,
  paintedLater,
  star,
} from '../paint';
import { add, byId, setTint } from '../util';
import { ViewPool } from '../views';
import { MOON_TINT, SUN_TINT } from './circadien';
import { MOON, crescentPath, sunPath } from './circadien-paint';

const RAY = {
  beats: 0.45,
  edge: 1.5,
  heart: 0.3,
  heartEdge: 0.6,
  calmAlpha: 0.55,
  calmSlower: 1.3,
};
const RAY_HEART = 0xfff6dc;
const ECLIPSE_LOOK = {
  sun: 16,
  moon: 17,
  sunGlow: 46,
  slide: { x: 46, y: -16 },
  growShare: 0.3,
  coverShare: 0.45,
  ring: 19,
  ringPulse: 3,
  shadowAlpha: 0.42,
  rimAlpha: 0.5,
  fadeShare: 0.4,
  corona: { from: 0.65, rise: 0.15, glow: 55, chargedGlow: 16, alpha: 0.75 },
  diamond: { angle: Math.PI * 0.82, size: 14, charged: 15 },
} as const;
// Beats after the burst, and lengths from the middle of the Éclipse.
const BURST = {
  beats: 0.8,
  reachShare: 0.22,
  narrowing: 0.6,
  glow: 2,
  glowAlpha: 0.25,
  heart: 0.35,
  wave: { from: 25, to: 270, width: 5, alpha: 0.8 },
  flare: { radius: 126, beats: 0.6, alpha: 0.6 },
  flash: { alpha: 0.16, beats: 0.25 },
  calmAlpha: 0.55,
  calmSlower: 1.3,
} as const;
// Night blue, never black: deeper at the edges than around the Éclipse.
const DARK = {
  color: 0x080c40,
  alpha: 0.7,
  calmAlpha: 0.3,
  core: 0x24349a,
  coreRadius: 600,
  coreAlpha: 0.45,
  rise: 0.6,
  lingers: 0.1,
  fadeBeats: 1,
  margin: 4000,
} as const;
// Where the stars of the darkness twinkle, in shares of the screen, and in which hue.
const DARK_STARS: readonly (readonly [x: number, y: number, size: number, hue: SparkleHue])[] = [
  [0.07, 0.12, 12, 'mage'],
  [0.33, 0.07, 10, 'turquoise'],
  [0.58, 0.16, 13, 'healer'],
  [0.86, 0.1, 11, 'superfan'],
  [0.16, 0.5, 11, 'gold'],
  [0.74, 0.42, 12, 'mage'],
  [0.93, 0.66, 10, 'turquoise'],
  [0.42, 0.86, 12, 'healer'],
  [0.8, 0.9, 11, 'superfan'],
];
const DARK_TWINKLE = 0.25;
const SHADOW = 0x101450;
const RING = 0xfff3c0;
const PAINTED = { sun: 16, moon: 17, sparkle: 16 } as const;
const SPARKLE_HUES = {
  shine: '#fff3c0',
  gold: '#ffc94d',
  mage: '#ff6fa8',
  turquoise: '#3fd0c9',
  healer: '#7cf2b0',
  superfan: '#c77dff',
} as const;
type SparkleHue = keyof typeof SPARKLE_HUES;

interface Ray {
  readonly playerId: PlayerId;
  readonly angle: number;
  readonly count: number;
  readonly half: number;
  readonly length: number;
  readonly spread: number;
  readonly tick: number;
}

interface EclipseView {
  readonly shadow: Graphics;
  readonly burst: Graphics;
  readonly burstGlow: Graphics;
  readonly crown: Graphics;
  readonly glow: Sprite;
  readonly corona: Sprite;
  readonly flare: Sprite;
  readonly sun: Sprite;
  readonly moon: Sprite;
  readonly diamond: Sprite;
}

function sun(ctx: Ctx): void {
  sunPath(ctx, PAINTED.sun, 1.2);
}

function moon(ctx: Ctx): void {
  circle(ctx, PAINTED.moon);
  ctx.fillStyle = '#0b0d2a';
  ctx.fill();
  ctx.lineWidth = 1.4;
  ctx.strokeStyle = MOON;
  ctx.stroke();
  ctx.save();
  ctx.translate(-5, -4);
  ctx.rotate(-0.6);
  crescentPath(ctx, 5);
  ctx.globalAlpha = 0.25;
  ctx.fillStyle = WHITE;
  ctx.fill();
  ctx.restore();
}

// Four long points and a white heart that runs into its hue.
function sparkle(hue: string): (ctx: Ctx) => void {
  return (ctx) => {
    const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, PAINTED.sparkle);
    gradient.addColorStop(0, WHITE);
    gradient.addColorStop(0.35, hue);
    gradient.addColorStop(1, 'rgb(0 0 0 / 0)');
    star(ctx, 4, PAINTED.sparkle, PAINTED.sparkle * 0.16);
    ctx.fillStyle = gradient;
    ctx.fill();
  };
}

const SKIES_PIECES = {
  sun: [64, 64, PAINTED.sun, sun],
  moon: [40, 40, PAINTED.moon, moon],
  shine: [36, 36, PAINTED.sparkle, sparkle(SPARKLE_HUES.shine)],
  gold: [36, 36, PAINTED.sparkle, sparkle(SPARKLE_HUES.gold)],
  mage: [36, 36, PAINTED.sparkle, sparkle(SPARKLE_HUES.mage)],
  turquoise: [36, 36, PAINTED.sparkle, sparkle(SPARKLE_HUES.turquoise)],
  healer: [36, 36, PAINTED.sparkle, sparkle(SPARKLE_HUES.healer)],
  superfan: [36, 36, PAINTED.sparkle, sparkle(SPARKLE_HUES.superfan)],
} satisfies Record<string, Piece>;

const ease = (share: number): number => smoothstep(clamp01(share));

// The strongest Éclipse of the frame darkens the arena around itself.
interface Sky {
  darkness: number;
  flash: number;
  x: number;
  y: number;
}

interface SkyView {
  readonly dark: Sprite;
  readonly core: Sprite;
  readonly stars: readonly Sprite[];
  readonly flash: Sprite;
}

interface Eclipse {
  readonly effect: EclipseEffect;
  readonly ways: readonly Vec2[];
}

export function createSkies(ctx: RenderContext): Family {
  const { layers, textures } = ctx;
  const attacks = new Map<string, CircadianAttack>();
  for (const [classId, attack] of ctx.attacks) {
    if (attack.kind === 'circadian') {
      attacks.set(classId, attack);
    }
  }
  const eclipses = new Map<string, Eclipse>();
  for (const [classId, effect] of ctx.skillEffects) {
    if (effect.kind === 'eclipse') {
      eclipses.set(classId, { effect, ways: eclipseRayWays(effect.rays.count) });
    }
  }
  const sheet = paintedLater((): Sheet<keyof typeof SKIES_PIECES> => paintSheet(SKIES_PIECES));
  const rays = new Map<number, Ray>();
  let nextRay = 0;

  const rayViews = new ViewPool<Graphics>(
    () => {
      const graphics = new Graphics();
      layers.fx.addChild(graphics);
      return graphics;
    },
    (graphics) => {
      graphics.visible = false;
    },
  );
  let eclipseViews: ViewPool<EclipseView> | undefined;
  let skyView: SkyView | undefined;
  const sky: Sky = { darkness: 0, flash: 0, x: 0, y: 0 };

  function eclipsePool(): ViewPool<EclipseView> {
    const { shapes } = sheet.get();
    eclipseViews ??= new ViewPool<EclipseView>(
      () => {
        const shadow = new Graphics();
        layers.zones.addChild(shadow);
        const burstGlow = layers.fx.addChild(new Graphics());
        burstGlow.blendMode = 'add';
        const burst = layers.fx.addChild(new Graphics());
        burst.blendMode = 'normal';
        const glow = add(layers.fx, textures.halo);
        const corona = add(layers.fx, textures.halo);
        const flare = add(layers.fx, textures.halo);
        const sun = add(layers.weapons, shapes.sun);
        const moon = add(layers.weapons, shapes.moon);
        const crown = layers.weapons.addChild(new Graphics());
        const diamond = add(layers.weapons, shapes.shine);
        return { shadow, burst, burstGlow, crown, glow, corona, flare, sun, moon, diamond };
      },
      (view) => {
        view.shadow.visible = view.burst.visible = view.burstGlow.visible = false;
        view.crown.visible = view.glow.visible = false;
        view.corona.visible = view.flare.visible = view.diamond.visible = false;
        view.sun.visible = view.moon.visible = false;
      },
    );
    return eclipseViews;
  }

  function skyOnce(): SkyView {
    const { shapes } = sheet.get();
    skyView ??= {
      dark: layers.dim.addChild(new Sprite(Texture.WHITE)),
      core: add(layers.dim, textures.halo),
      stars: DARK_STARS.map(([, , , hue]) => add(layers.dim, shapes[hue])),
      flash: layers.screen.addChild(new Sprite(Texture.WHITE)),
    };
    return skyView;
  }

  function drawRay(id: number, ray: Ray, state: SimState, alpha: number, frame: Frame): boolean {
    const { calm, light, palette, tempo } = frame;
    const player = byId(state.players, ray.playerId);
    const life = RAY.beats * tempo.ticksPerBeat * (calm ? RAY.calmSlower : 1);
    const share = (frame.now - 1 - ray.tick) / life;
    if (player === undefined || share >= 1) {
      return false;
    }
    const left = 1 - Math.max(0, share);
    const graphics = rayViews.acquire(id);
    const x = lerp(player.prevX, player.x, alpha);
    const y = lerp(player.prevY, player.y, alpha);
    const aim = { x: Math.cos(ray.angle), y: Math.sin(ray.angle) };
    const color = palette[ctx.classTokens.get(player.classId) ?? 'or'];
    const strength = calm ? RAY.calmAlpha : 1;
    graphics.visible = true;
    graphics.position.set(x, y);
    graphics.blendMode = light.additive ? 'add' : 'normal';
    graphics.clear();
    for (let rank = 0; rank < ray.count; rank += 1) {
      const way = rayWay(aim, rank, ray.count, ray.spread);
      const toX = way.x * ray.length;
      const toY = way.y * ray.length;
      const width = 2 * ray.half * left + RAY.edge;
      if (!light.additive) {
        graphics
          .moveTo(0, 0)
          .lineTo(toX, toY)
          .stroke({
            width: width + 3,
            color: palette.texte,
            alpha: 0.35 * left * strength,
            cap: 'round',
          });
      }
      graphics
        .moveTo(0, 0)
        .lineTo(toX, toY)
        .stroke({ width, color, alpha: left * strength, cap: 'round' });
      if (!calm) {
        graphics
          .moveTo(0, 0)
          .lineTo(toX, toY)
          .stroke({
            width: 2 * ray.half * RAY.heart * left + RAY.heartEdge,
            color: RAY_HEART,
            alpha: left,
            cap: 'round',
          });
      }
    }
    return true;
  }

  function drawEclipse(prop: PropState, eclipse: Eclipse, state: SimState, frame: Frame): void {
    const { calm, light, tempo } = frame;
    const { effect } = eclipse;
    const view = eclipsePool().acquire(prop.id);
    const owner = byId(state.players, prop.playerId);
    const radius = owner === undefined ? effect.radius : eclipseRadius(owner, effect);
    const sliding = prop.phase === 'flying';
    const between = frame.now - state.tick;
    const slideTicks = effect.beats * tempo.ticksPerBeat - 1;
    const share = sliding ? clamp01((slideTicks - prop.ticksLeft + between) / slideTicks) : 1;
    const after = sliding
      ? 0
      : (effect.slowBeats * tempo.ticksPerBeat - prop.ticksLeft + between) / tempo.ticksPerBeat;
    const grow = Math.min(1, share / ECLIPSE_LOOK.growShare);
    const covered = clamp01((share - ECLIPSE_LOOK.growShare) / ECLIPSE_LOOK.coverShare);
    const shown = Math.max(0, 1 - after / (effect.slowBeats * ECLIPSE_LOOK.fadeShare));
    const eased = smoothstep(covered);
    const { corona: crown, diamond } = ECLIPSE_LOOK;
    const corona = ease((share - crown.from) / crown.rise);
    const charge = clamp01((share - crown.from) / (1 - crown.from)) ** 2;
    const halo = light.haloAlpha * (calm ? 0.6 : 1);

    view.glow.visible = shown > 0;
    view.glow.position.set(prop.x, prop.y);
    view.glow.scale.set((ECLIPSE_LOOK.sunGlow * grow) / textures.halo.radius);
    view.glow.alpha = 0.5 * shown * (1 - eased * 0.6) * halo;
    setTint(view.glow, SUN_TINT);

    view.corona.visible = shown > 0 && corona > 0;
    view.corona.position.set(prop.x, prop.y);
    view.corona.scale.set((crown.glow + crown.chargedGlow * charge) / textures.halo.radius);
    view.corona.alpha = crown.alpha * corona * shown * halo;
    setTint(view.corona, RING);

    view.sun.visible = shown > 0 && grow > 0;
    view.sun.position.set(prop.x, prop.y);
    view.sun.scale.set((ECLIPSE_LOOK.sun * grow) / PAINTED.sun);
    view.sun.alpha = shown;

    view.moon.visible = shown > 0 && covered > 0;
    view.moon.position.set(
      prop.x + ECLIPSE_LOOK.slide.x * (1 - eased),
      prop.y + ECLIPSE_LOOK.slide.y * (1 - eased),
    );
    view.moon.scale.set(ECLIPSE_LOOK.moon / PAINTED.moon);
    view.moon.alpha = shown * Math.min(1, covered * 3);

    view.diamond.visible = shown > 0 && corona > 0;
    view.diamond.position.set(
      prop.x + Math.cos(diamond.angle) * ECLIPSE_LOOK.ring,
      prop.y + Math.sin(diamond.angle) * ECLIPSE_LOOK.ring,
    );
    view.diamond.scale.set((diamond.size + diamond.charged * charge) / PAINTED.sparkle);
    view.diamond.rotation = calm ? 0 : 0.2 + 0.6 * charge;
    view.diamond.alpha = Math.min(1, corona * (0.75 + 0.6 * charge)) * shown;

    const flare = sliding ? 0 : clamp01(1 - after / BURST.flare.beats) ** 2;
    view.flare.visible = flare > 0;
    view.flare.position.set(prop.x, prop.y);
    view.flare.scale.set(BURST.flare.radius / textures.halo.radius);
    view.flare.alpha = BURST.flare.alpha * flare * halo;
    setTint(view.flare, SUN_TINT);

    drawShadow(view.shadow, prop, radius, sliding, after / effect.slowBeats, frame);
    drawCrown(view.crown, prop, shown * corona, frame);
    drawBurst(view, prop, eclipse, sliding ? -1 : after, frame);

    const darkness = sliding
      ? ease(share / (calm ? 1 : DARK.rise))
      : 1 - ease((after - DARK.lingers) / (DARK.fadeBeats * (calm ? 2 : 1)));
    if (darkness > sky.darkness) {
      sky.darkness = darkness;
      sky.x = prop.x;
      sky.y = prop.y;
    }
    if (!sliding && !calm) {
      sky.flash = Math.max(sky.flash, clamp01(1 - after / BURST.flash.beats));
    }
  }

  function drawShadow(
    shadow: Graphics,
    prop: PropState,
    radius: number,
    sliding: boolean,
    share: number,
    frame: Frame,
  ): void {
    shadow.visible = !sliding;
    shadow.clear();
    if (sliding) {
      return;
    }
    const opening = frame.calm ? 1 : Math.min(1, share * 6);
    const fading = Math.min(1, (1 - share) * 4);
    shadow.position.set(prop.x, prop.y);
    shadow
      .circle(0, 0, radius * opening)
      .fill({ color: SHADOW, alpha: ECLIPSE_LOOK.shadowAlpha * fading })
      .stroke({ width: 1.5, color: MOON_TINT, alpha: ECLIPSE_LOOK.rimAlpha * fading });
  }

  // The ring of light around the covered sun.
  function drawCrown(crown: Graphics, prop: PropState, ring: number, frame: Frame): void {
    const { calm } = frame;
    crown.visible = ring > 0;
    crown.clear();
    if (ring <= 0) {
      return;
    }
    const pulse = calm ? 0 : ECLIPSE_LOOK.ringPulse * frame.pulse;
    crown.position.set(prop.x, prop.y);
    crown
      .circle(0, 0, ECLIPSE_LOOK.ring + pulse)
      .stroke({ width: 2.5, color: RING, alpha: (calm ? 0.5 : 0.95) * ring });
  }

  // Twelve rays of sun out of the covered sun, through the screen, and a wave of light.
  function drawBurst(
    { burst, burstGlow }: EclipseView,
    prop: PropState,
    { effect, ways }: Eclipse,
    after: number,
    frame: Frame,
  ): void {
    const { calm } = frame;
    const share = after / (BURST.beats * (calm ? BURST.calmSlower : 1));
    burst.visible = burstGlow.visible = share >= 0 && share < 1;
    burst.clear();
    burstGlow.clear();
    if (!burst.visible) {
      return;
    }
    const left = 1 - share;
    const strength = calm ? BURST.calmAlpha : 1;
    const reach = effect.rays.length * ease(share / BURST.reachShare);
    const width = effect.rays.width * (1 - BURST.narrowing * share);
    burst.position.set(prop.x, prop.y);
    burstGlow.position.set(prop.x, prop.y);
    for (const way of ways) {
      const fromX = way.x * ECLIPSE_LOOK.ring;
      const fromY = way.y * ECLIPSE_LOOK.ring;
      const toX = way.x * (ECLIPSE_LOOK.ring + reach);
      const toY = way.y * (ECLIPSE_LOOK.ring + reach);
      burst
        .moveTo(fromX, fromY)
        .lineTo(toX, toY)
        .stroke({ width, color: SUN_TINT, alpha: left * strength, cap: 'round' });
      if (calm) {
        continue;
      }
      burstGlow
        .moveTo(fromX, fromY)
        .lineTo(toX, toY)
        .stroke({
          width: width * BURST.glow,
          color: SUN_TINT,
          alpha: BURST.glowAlpha * left,
          cap: 'round',
        });
      burst
        .moveTo(fromX, fromY)
        .lineTo(toX, toY)
        .stroke({ width: width * BURST.heart + 0.8, color: RAY_HEART, alpha: left, cap: 'round' });
    }
    const { wave } = BURST;
    burstGlow
      .circle(0, 0, lerp(wave.from, wave.to, ease(share)))
      .stroke({ width: wave.width, color: RING, alpha: wave.alpha * left * strength });
  }

  function drawSky(state: SimState, frame: Frame): void {
    if (skyView === undefined && sky.darkness <= 0) {
      return;
    }
    const { dark, core, stars, flash } = skyOnce();
    const { calm, camera, tempo } = frame;
    dark.visible = core.visible = sky.darkness > 0;
    dark.tint = DARK.color;
    dark.alpha = sky.darkness * (calm ? DARK.calmAlpha : DARK.alpha);
    dark.position.set(-DARK.margin, -DARK.margin);
    dark.width = state.arena.width + 2 * DARK.margin;
    dark.height = state.arena.height + 2 * DARK.margin;
    core.position.set(sky.x, sky.y);
    core.scale.set(DARK.coreRadius / textures.halo.radius);
    core.alpha = sky.darkness * DARK.coreAlpha * (calm ? 0.5 : 1);
    setTint(core, DARK.core);
    const seenWidth = camera.screenWidth / camera.scale;
    const seenHeight = camera.screenHeight / camera.scale;
    const beats = frame.now / tempo.ticksPerBeat;
    DARK_STARS.forEach(([x, y, size], index) => {
      const twinkled = stars[index];
      if (twinkled === undefined) {
        return;
      }
      const twinkle = calm ? 1 : 1 + DARK_TWINKLE * Math.sin((beats + index * 0.45) * Math.PI);
      twinkled.visible = sky.darkness > 0;
      twinkled.position.set(
        camera.centerX + (x - 0.5) * seenWidth,
        camera.centerY + (y - 0.5) * seenHeight,
      );
      twinkled.scale.set((size * twinkle) / PAINTED.sparkle);
      twinkled.alpha = 0.8 * sky.darkness;
    });
    flash.visible = sky.flash > 0;
    flash.tint = RING;
    flash.alpha = BURST.flash.alpha * sky.flash;
    flash.width = camera.screenWidth;
    flash.height = camera.screenHeight;
  }

  return {
    onEvent(event: SimEvent, state: SimState, frame: Frame): void {
      if (event.type !== 'playerFired') {
        return;
      }
      const player = byId(state.players, event.playerId);
      const attack = player === undefined ? undefined : attacks.get(player.classId);
      if (player === undefined || attack === undefined) {
        return;
      }
      const halfBars = frame.portrait ? 1 : attack.halfBars;
      if (!skyOf(state.tick, frame.tempo, player, { halfBars }).sun) {
        return;
      }
      rays.set(nextRay, {
        playerId: player.id,
        angle: event.angle,
        count: starCount(player, 1),
        half: statValue(player, 'attackRadiusMul', attack.ray.width / 2),
        length: attack.ray.length,
        spread: attack.ray.spreadDegrees,
        tick: state.tick,
      });
      nextRay += 1;
    },
    reset(): void {
      rays.clear();
      rayViews.releaseAll();
      eclipseViews?.releaseAll();
      sky.darkness = sky.flash = 0;
      if (skyView !== undefined) {
        skyView.dark.visible = skyView.core.visible = skyView.flash.visible = false;
        for (const twinkled of skyView.stars) {
          twinkled.visible = false;
        }
      }
    },
    update(state: SimState, alpha: number, frame: Frame): void {
      if (attacks.size === 0 && eclipses.size === 0) {
        return;
      }
      rayViews.begin();
      for (const [id, ray] of rays) {
        if (!drawRay(id, ray, state, alpha, frame)) {
          rays.delete(id);
        }
      }
      rayViews.end();
      if (eclipses.size === 0) {
        return;
      }
      const pool = eclipsePool();
      pool.begin();
      sky.darkness = sky.flash = 0;
      for (const prop of state.props ?? []) {
        const owner = prop.kind === ECLIPSE ? byId(state.players, prop.playerId) : undefined;
        const eclipse = owner === undefined ? undefined : eclipses.get(owner.classId);
        if (eclipse !== undefined) {
          drawEclipse(prop, eclipse, state, frame);
        }
      }
      pool.end();
      drawSky(state, frame);
    },
    destroy(): void {
      rays.clear();
      sheet.painted?.source.destroy();
    },
  };
}
